import React, { useContext } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { FormikWithExitHandler } from 'src/Libs/form'
import { SettingsBox } from 'src/Elements/Data'
import { NumberField } from 'src/Libs/inputs/formik'
import { UnitTypes } from 'src/Libs/types'
import { Box, Typography, useTheme } from '@mui/material'
import SettingsFormContainer from 'src/Elements/SystemSettings/forms/SettingsFormContainer'
import { LinkButton } from '@e3dc-react/shell/Elements/Buttons'
import { makeStyles } from 'tss-react/mui'
import { HIDE_ON_PRINT_CLASS } from 'src/Libs/chart-kit/shared/utils/print-chart-data'
import { DynamicPowerPricesEnabledContext } from 'src/Libs/dynamic-power-prices/context/dynamic-power-prices-enabled-context'
import { DynamicPricesSettings, DynamicPricesSettingsForm, DynamicPriceType } from 'src/Libs/dynamic-power-prices/types'
import { mergePriceLimitsIntoSettings } from 'src/Libs/dynamic-power-prices/utils/price-limit-settings'
import { InstallationOnlineStatusContext } from 'src/Libs/installations/context/installation-online-status-context'
import { OfflineOverlay } from 'src/Elements/Overlays'
import {
  useIsDynamicEmsAccessible,
  useNavigateToSmartChargeDppSettings,
  usePowerPriceLimitSettingsFormInitializer,
} from 'src/Libs/dynamic-power-prices/hooks'
import BoxWrapper from 'src/Elements/Utilities/BoxWrapper'
import { Ai360, B4RemainingTime } from '@e3dc-react/icons'
import { Stack } from '@mui/system'
import { isFlowTheme } from 'src/Libs/utils'
import { useNavigate } from 'react-router-dom'
import { useTypedSelector } from 'src/State/RootReducer'
import { useFarmContext } from 'src/Elements/Farm'

export interface PowerPriceChartModalProps {
  disabled: boolean
  onClose?: () => void
  selectedDynamicPowerPriceModalSetting: DynamicPriceType
  dynamicPricesSettings: DynamicPricesSettings | null
  handleDynamicPricesSettingsUpdate: (newValues: DynamicPricesSettings) => Promise<void>
  isDynamicEmsEnabled: boolean | undefined
  isDynamicEmsDataLoading: boolean
}

const CUSTOM_XS_BREAKPOINT = 400

const useStyles = makeStyles()(theme => ({
  systemSettingsLink: {
    display: 'flex',
    justifyContent: 'center',
    width: 'max-content',
    margin: '0 auto',
    '&:hover': {
      cursor: 'pointer',
    },

    [theme.breakpoints.down(CUSTOM_XS_BREAKPOINT)]: {
      marginTop: 0,
    },
  },
  wrapper: {
    height: 'max-content',

    '& form ': {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      width: '100%',
      height: '100%',
    },
    '& .MuiFormControl-root': {
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
    },
  },
  input: {
    '& .MuiInputBase-root': {
      display: 'flex',
      alignContent: 'center',
      flexWrap: 'nowrap',
      alignItems: 'baseline',
      margin: 0,
    },
  },
  settingsBoxWrapper: {
    marginBottom: 0,
    [theme.breakpoints.down(CUSTOM_XS_BREAKPOINT)]: {
      '& *': { fontSize: 12 },
    },
  },
  offlineIcon: {
    top: theme.spacing(1),
    right: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
      top: theme.spacing(1),
      right: theme.spacing(1),
    },
    [theme.breakpoints.down('sm')]: {
      top: theme.spacing(1),
      right: theme.spacing(1),
    },
  },
  dynamicEmsTitle: {
    display: 'flex',
    flexDirection: 'row',
    margin: theme.spacing(0, 2, 2),
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  dynamicEmsDescription: {
    margin: theme.spacing(0, 2),
  },
  /* Override the padding around the submit-buttons */
  stackContainerStyles: {
    padding: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      paddingBottom: theme.spacing(0),
    },
  },
}))

/**
 * Form to set the power price limit
 */
const PowerPriceLimitSettingsForm: React.FC<PowerPriceChartModalProps> = ({
  dynamicPricesSettings,
  handleDynamicPricesSettingsUpdate,
  disabled,
  onClose,
  selectedDynamicPowerPriceModalSetting,
  isDynamicEmsEnabled,
  isDynamicEmsDataLoading,
}) => {
  const { t } = useCustomTranslation()
  const installationId = useTypedSelector(s => s.storage.installation?.id) || ''
  const storageId = useTypedSelector(s => s.storage.activeStorage) || ''

  const { classes } = useStyles()
  const navigate = useNavigate()
  const { navigateToSmartChargeDppSettings } = useNavigateToSmartChargeDppSettings()
  const theme = useTheme()
  const { initialValues, validationSchema } = usePowerPriceLimitSettingsFormInitializer(dynamicPricesSettings)
  const { isDynamicEmsV2Accessible } = useIsDynamicEmsAccessible()
  const canUseAI360V2 = isDynamicEmsV2Accessible && !isFlowTheme

  const { isDynamicPowerPriceEnabledForBattery, isDynamicPowerPriceEnabledForWallboxes } = useContext(DynamicPowerPricesEnabledContext)
  const isBothDynamicPowerPriceSettingsEnabled =
    (isDynamicPowerPriceEnabledForBattery || isDynamicEmsEnabled) && isDynamicPowerPriceEnabledForWallboxes
  const showBatterySetting = isBothDynamicPowerPriceSettingsEnabled
    ? selectedDynamicPowerPriceModalSetting === DynamicPriceType.Battery
    : isDynamicPowerPriceEnabledForBattery
  const showWallboxSetting = isBothDynamicPowerPriceSettingsEnabled
    ? selectedDynamicPowerPriceModalSetting === DynamicPriceType.Wallbox
    : isDynamicPowerPriceEnabledForWallboxes

  const showDynamicEmsHint = selectedDynamicPowerPriceModalSetting === DynamicPriceType.Battery && isDynamicEmsEnabled && !isDynamicEmsDataLoading
  const showDynamicEmsLoadingHint =
    selectedDynamicPowerPriceModalSetting === DynamicPriceType.Battery && isDynamicEmsEnabled && isDynamicEmsDataLoading

  const updateLimit = (values: DynamicPricesSettingsForm): void => {
    handleDynamicPricesSettingsUpdate(mergePriceLimitsIntoSettings(dynamicPricesSettings, values))
  }

  const redirectAndClose = (): void => {
    if (onClose) onClose()

    if (canUseAI360V2) {
      navigate(`/dashboard/ai360/${installationId}/${storageId}`)
    } else {
      navigateToSmartChargeDppSettings()
    }
  }

  const { isStorageOnline, isServerOnline } = useContext(InstallationOnlineStatusContext)
  const { isFarm } = useFarmContext()
  const isOffline = (isFarm && !isServerOnline) || (!isFarm && !isStorageOnline)

  return (
    <FormikWithExitHandler
      formKey="power-price-limit-settings-form"
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={updateLimit}
      enableReinitialize={false}
    >
      <Box className={`${classes.wrapper} ${HIDE_ON_PRINT_CLASS}`}>
        <SettingsFormContainer
          model={initialValues}
          hasDivider
          containerStyle={{ p: 0, marginTop: 'auto' }}
          stackContainerStyles={classes.stackContainerStyles}
          rightControlsStyles={{
            flexDirection: { xs: 'row' },
            justifyContent: 'flex-end',

            [theme.breakpoints.down('md')]: {
              /* the two '&' symbols are there to increase the specificity */
              '&& > *': {
                width: 'max-content',
              },
            },
          }}
        >
          <Box>
            {!isFlowTheme && showWallboxSetting && (
              <OfflineOverlay isOffline={isOffline} offlineIconStyle={classes.offlineIcon}>
                <SettingsBox
                  title={t('systemSettings.dynamic-prices.price-limit-for-ev-charging.title')}
                  disabled={disabled}
                  suffix={
                    <NumberField
                      className={classes.input}
                      name={'priceLimitWallbox'}
                      suffix={UnitTypes.centsPerKiloWattHour}
                      isDecimal
                      step={0.001}
                      precision={3}
                      disabled={disabled}
                      ignoreNonNumerics
                    />
                  }
                  className={classes.settingsBoxWrapper}
                />
              </OfflineOverlay>
            )}
            {!isFlowTheme && !isDynamicEmsEnabled && showBatterySetting && (
              <OfflineOverlay isOffline={isOffline} offlineIconStyle={classes.offlineIcon}>
                <SettingsBox
                  title={t('systemSettings-dynamic-prices.battery.price-limit-for-battery-charging.title')}
                  disabled={disabled}
                  suffix={
                    <NumberField
                      className={classes.input}
                      name={'priceLimitBattery'}
                      suffix={UnitTypes.centsPerKiloWattHour}
                      isDecimal
                      step={0.001}
                      precision={3}
                      disabled={disabled}
                      ignoreNonNumerics
                    />
                  }
                  className={classes.settingsBoxWrapper}
                />
              </OfflineOverlay>
            )}
            {showDynamicEmsHint && (
              <OfflineOverlay isOffline={isOffline} offlineIconStyle={classes.offlineIcon}>
                <BoxWrapper>
                  <Stack className={classes.dynamicEmsTitle}>
                    <Ai360 />
                  </Stack>
                  <Typography className={classes.dynamicEmsDescription} variant="subtitle2">
                    {t('systemSettings-dynamic-prices.battery.dynamic-ems.description')}
                  </Typography>
                </BoxWrapper>
              </OfflineOverlay>
            )}
            {showDynamicEmsLoadingHint && (
              <OfflineOverlay isOffline={isOffline} offlineIconStyle={classes.offlineIcon}>
                <BoxWrapper>
                  <Stack sx={{ display: 'flex', flexDirection: 'row' }}>
                    <B4RemainingTime sx={{ width: 24, height: 24, marginTop: 'auto', marginBottom: 'auto' }} />
                    <Stack sx={{ display: 'flex', flexDirection: 'column', marginLeft: 2 }}>
                      <Typography variant="h3">{t('systemSettings-dynamic-prices.battery.dynamic-ems.loading.title')}</Typography>
                      <Typography variant="subtitle2">{t('systemSettings-dynamic-prices.battery.dynamic-ems.loading.description')}</Typography>
                    </Stack>
                  </Stack>
                </BoxWrapper>
              </OfflineOverlay>
            )}
            {!isOffline && (
              <LinkButton
                type="button"
                linkClassName={classes.systemSettingsLink}
                hasUnderline={false}
                onClick={redirectAndClose}
                content={
                  <Typography color={'primary'} variant="overline">
                    {canUseAI360V2 ? t('ai360.redirect.home-page') : t('dashboard.liveView.widgets.dynamic-power-price.link-to-system-settings')}
                  </Typography>
                }
              />
            )}
          </Box>
        </SettingsFormContainer>
      </Box>
    </FormikWithExitHandler>
  )
}

export default PowerPriceLimitSettingsForm
